import { FC, useRef } from "react";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "motion/react";
import { IMDSSessionProps } from "@/mds/components/sessions/session.types";
import { MDSCombobox, MDSComboboxColor, MDSComboboxSubmitHandler } from "@/mds/components/forms";
import { MdsIconKind } from "@/design-system/components/icon";
import { SessionViewMode } from "@/mds/components/sessions/session.constants";
import { MDSSessionContentRenderer } from "@/mds/components/sessions/session-content/session-content-renderer";
import { MemCommonMentionKind } from "@mem-labs/common-editor";
import { SessionArtifactViewer } from "@/mds/components/sessions/session-parts/artifacts/viewer/session-artifact-viewer";
import { useMDSSession } from "@/mds/components/sessions/session-context/mds-session-context";
import { MDSSessionCountdownTimer } from "@/mds/components/sessions/session-content/countdown-timer";
import {
  StyledSessionTitle,
  StyledTitleAndStatusContainer,
  StyledTitleText,
} from "@/mds/components/sessions/session.style";

interface Props extends IMDSSessionProps {}

export const MDSSessionContent: FC<Props> = ({
  sessionTitle,
  handlers: { handleFetchComboboxData, handleInputFormSubmit },
}) => {
  const {
    activeArtifact,
    sessionViewMode,
    countdownDurationMs,
    isCountdownPaused,
    isCountdownVisible,
    isInputFormVisible,
    isSessionTitleVisible,
    isSessionViewExpanded,
    onCountdownComplete,
    handleHoverExtension,
    handleUnhoverExtension,
    handleInputFocus,
    handleInputBlur,
    handleInputChange,
    stopCountdown,
    expandFromNotification,
  } = useMDSSession();

  const containerRef = useRef<HTMLDivElement>(null);

  const handleComboboxSubmit: MDSComboboxSubmitHandler = ({ mdxContent, mentions }) => {
    stopCountdown();

    // TODO - Clean this up.
    const mentionedCollections = mentions
      .filter(mention => mention.kind === MemCommonMentionKind.Collection)
      .map(mention => ({
        kind: "COLLECTION" as const,
        collection_id: mention.collection_id,
        collection_title: mention.collection_title,
      }));

    const mentionedNotes = mentions
      .filter(mention => mention.kind === MemCommonMentionKind.Note)
      .map(mention => ({
        kind: "NOTE" as const,
        note_id: mention.note_id,
        note_primary_label: mention.note_primary_label,
      }));

    handleInputFormSubmit({
      content: mdxContent,
      context: {
        // TODO - Clean this up.
        mentions: [...mentionedCollections, ...mentionedNotes],
      },
    });
  };

  const isMinimized = sessionViewMode === SessionViewMode.Minimized;
  const isNotificationMode = sessionViewMode === SessionViewMode.Notification;

  const handleNotificationClick = () => {
    if (isNotificationMode) {
      expandFromNotification();
    }
  };

  return (
    <AnimatePresence mode="wait">
      {!isMinimized && (
        <ResponsiveContainer
          ref={containerRef}
          isExpanded={isSessionViewExpanded}
          onMouseEnter={handleHoverExtension}
          onMouseLeave={handleUnhoverExtension}
          onClick={handleNotificationClick}
          style={{
            cursor: isNotificationMode ? "pointer" : "default",
          }}
          initial={{ opacity: 0, height: "auto" }}
          animate={{
            opacity: 1,
            height: "auto",
            transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] },
          }}
          exit={{
            opacity: 0,
            transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
          }}
        >
          <ResponsiveContentContainer isExpanded={isSessionViewExpanded}>
            <AnimatedContainer
              isExpanded={isSessionViewExpanded}
              animate={{
                height: "100%",
              }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            >
              {isCountdownVisible && (
                <MDSSessionCountdownTimer
                  isPaused={isCountdownPaused}
                  durationMs={countdownDurationMs}
                  onComplete={onCountdownComplete}
                />
              )}

              {isSessionTitleVisible && (
                <StyledSessionTitle>
                  <StyledTitleAndStatusContainer>
                    <StyledTitleText>{sessionTitle}</StyledTitleText>
                  </StyledTitleAndStatusContainer>
                </StyledSessionTitle>
              )}

              <SessionArtifactViewer isVisible={!!activeArtifact} />

              <MDSSessionContentRenderer mode={sessionViewMode} />

              {isInputFormVisible && (
                <StyledSection>
                  <StyledForm
                    onSubmit={e => {
                      e.preventDefault();
                    }}
                  >
                    <MDSCombobox
                      placeholder="Anything else?"
                      fetchData={handleFetchComboboxData}
                      leftIcon={MdsIconKind.Sparkle}
                      color={MDSComboboxColor.Blue}
                      onSubmit={handleComboboxSubmit}
                      popoverProps={{
                        shouldFlip: false,
                        style: {
                          maxWidth: "365px",
                        },
                      }}
                      onInputFocus={handleInputFocus}
                      onInputBlur={handleInputBlur}
                      onInputChange={handleInputChange}
                    />
                  </StyledForm>
                </StyledSection>
              )}
            </AnimatedContainer>
          </ResponsiveContentContainer>
        </ResponsiveContainer>
      )}
    </AnimatePresence>
  );
};

const ResponsiveContainer = styled(motion.section)<{ isExpanded: boolean }>`
  width: ${({ isExpanded }) => (isExpanded ? "400px" : "min-content")};
  min-width: ${({ isExpanded }) => (isExpanded ? "400px" : "min-content")};
  height: 100%;
  border: 1px solid #e0e0e0;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  background: ${({ theme }) => theme.colors.white};
  border-radius: 16px;
  max-height: 800px;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  box-shadow:
    0 24px 48px rgba(0, 0, 0, 0.2),
    0 8px 16px rgba(0, 0, 0, 0.1);
  backface-visibility: hidden;
`;

const ResponsiveContentContainer = styled.div<{ isExpanded: boolean }>`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const AnimatedContainer = styled(motion.div)<{ isExpanded: boolean }>`
  height: 100%;
  width: 100%;
  will-change: height;
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  position: relative;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  overflow: hidden;
`;

export const StyledSection = styled.section`
  width: 100%;
  padding-left: ${({ theme }) => `${theme.spacing.md}`};
  padding-right: ${({ theme }) => `${theme.spacing.md}`};
  padding-bottom: ${({ theme }) => `${theme.spacing.md}`};
  padding-top: 8px;
  min-height: 60px;
`;

export const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
